import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
  Drawer, IconButton, InputAdornment, Link, Snackbar, Tab, Tabs,
  TextField, Tooltip, Typography, Paper,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import TodayIcon from '@mui/icons-material/Today';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import SavePhoneDialog from '@/components/admin/dialpad/SavePhoneDialog';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';

import { phappsRequest } from '@/services/apiService';
import { API_PATHS } from '@/config/apiConstants';

const CALENDLY_BLUE  = '#006BFF';
const CANCELED_COLOR = '#ef4444';
const PERTH_TZ       = 'Australia/Perth';

// Default visible slot range — expand via banner if events fall outside
const DEFAULT_SLOT_MIN = '06:00:00';
const DEFAULT_SLOT_MAX = '20:00:00';

const LOCATION_ICONS = {
  zoom:                          <VideocamOutlinedIcon fontSize="small" />,
  google_conference:             <VideocamOutlinedIcon fontSize="small" />,
  microsoft_teams_conference:    <VideocamOutlinedIcon fontSize="small" />,
  gotomeeting:                   <VideocamOutlinedIcon fontSize="small" />,
  webex:                         <VideocamOutlinedIcon fontSize="small" />,
  physical:                      <PlaceOutlinedIcon fontSize="small" />,
  outbound_call:                 <PhoneOutlinedIcon fontSize="small" />,
  inbound_call:                  <PhoneOutlinedIcon fontSize="small" />,
  custom:                        <PlaceOutlinedIcon fontSize="small" />,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtPerthDateTime(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: PERTH_TZ,
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(iso));
}

function fmtPerthTime(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: PERTH_TZ, hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(iso));
}

function fmtShortDate(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: PERTH_TZ, weekday: 'short', day: 'numeric', month: 'short',
  }).format(new Date(iso));
}

function formatAuPhone(raw) {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, '');
  let local = digits;
  if (local.startsWith('61') && local.length >= 11) local = '0' + local.slice(2);
  if (local.startsWith('04') && local.length === 10)
    return `+61 ${local.slice(1, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
  if (local.startsWith('0') && local.length === 10)
    return `+61 ${local[1]} ${local.slice(2, 6)} ${local.slice(6)}`;
  return raw;
}

function looksLikePhone(str) {
  if (!str) return false;
  const d = String(str).replace(/\D/g, '');
  return d.length >= 7 && d.length <= 15 && String(str).length <= 25;
}

function getPharmacyName(ev) {
  const qa = ev.invitees?.[0]?.questions_answers ?? [];
  const QUESTIONS = ['pharmacy name', 'pharmacy/group name(s)', 'pharmacy/group name'];
  const m = qa.find(q => QUESTIONS.includes((q.question ?? '').toLowerCase().trim()));
  return m?.answer?.trim() || null;
}

function getPerthHour(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const perth = new Intl.DateTimeFormat('en-AU', { timeZone: PERTH_TZ, hour: 'numeric', hour12: false }).format(d);
  return Number(perth);
}

// ── Small reusable buttons ────────────────────────────────────────────────────

function PhoneButton({ phone }) {
  if (!phone) return null;
  return (
    <Tooltip title={`Call ${phone}`}>
      <IconButton size="small" component="a" href={`tel:${String(phone).replace(/\D/g, '')}`}
        sx={{ border: `1px solid ${CALENDLY_BLUE}`, borderRadius: 1, color: CALENDLY_BLUE, p: 0.4, ml: 0.5 }}>
        <PhoneOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function EmailButton({ email }) {
  if (!email) return null;
  return (
    <Tooltip title={`Email ${email}`}>
      <IconButton size="small" component="a" href={`mailto:${email}`}
        sx={{ border: `1px solid ${CALENDLY_BLUE}`, borderRadius: 1, color: CALENDLY_BLUE, p: 0.4, ml: 0.5 }}>
        <EmailOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// ── Upcoming strip ────────────────────────────────────────────────────────────

function UpcomingStrip({ events, onSelect }) {
  const [open, setOpen] = useState(false);

  const upcoming = useMemo(() => {
    const now = new Date();
    return events
      .filter(ev => ev.status !== 'canceled' && new Date(ev.start_time_utc) > now)
      .sort((a, b) => new Date(a.start_time_utc) - new Date(b.start_time_utc))
      .slice(0, 6);
  }, [events]);

  if (upcoming.length === 0) return null;

  return (
    <Box sx={{ mb: 1.5, flexShrink: 0 }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: open ? 1 : 0, userSelect: 'none' }}
      >
        <TodayIcon sx={{ fontSize: 16, color: CALENDLY_BLUE }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: CALENDLY_BLUE, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Upcoming ({upcoming.length})
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: '#9ca3af' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#9ca3af' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
          {upcoming.map(ev => {
            const pharmacy = getPharmacyName(ev);
            const label    = pharmacy ?? ev.invitees?.[0]?.name ?? ev.event_name;
            const color    = ev.manager?.accent_color ?? CALENDLY_BLUE;
            return (
              <Paper
                key={ev.id}
                elevation={0}
                onClick={() => onSelect(ev)}
                sx={{
                  flexShrink: 0, minWidth: 150, maxWidth: 180, p: 1.25, borderRadius: 2,
                  border: `1.5px solid ${color}33`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: color, boxShadow: `0 2px 8px ${color}22` },
                  transition: 'all 0.15s',
                }}
              >
                <Box sx={{ width: 24, height: 3, borderRadius: 1, bgcolor: color, mb: 0.75 }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }} noWrap>{label}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#6b7280' }}>{fmtShortDate(ev.start_time_utc)}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#6b7280' }}>{fmtPerthTime(ev.start_time_utc)}</Typography>
                {ev.manager?.name && (
                  <Typography sx={{ fontSize: '0.62rem', color, fontWeight: 600, mt: 0.5 }} noWrap>{ev.manager.name}</Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Event drawer ──────────────────────────────────────────────────────────────

function EventDrawer({ event, onClose, onChemistConnected, allChemists, chemistsLoading }) {
  const navigate = useNavigate();
  const [showChemistSearch, setShowChemistSearch] = useState(false);
  const [chemistQuery, setChemistQuery]           = useState('');
  const [connecting, setConnecting]               = useState(false);
  const [connectError, setConnectError]           = useState(null);
  const [savePhoneData, setSavePhoneData]         = useState(null);

  useEffect(() => {
    setShowChemistSearch(false);
    setChemistQuery('');
    setConnectError(null);
  }, [event?.id]);

  const filteredChemists = useMemo(() => {
    const q = chemistQuery.trim().toLowerCase();
    if (!q) return allChemists;
    return allChemists.filter(c => (c.chemistName || '').toLowerCase().includes(q));
  }, [allChemists, chemistQuery]);

  if (!event) return null;

  const canceled      = event.status === 'canceled';
  const accentColor   = canceled ? CANCELED_COLOR : (event.manager?.accent_color ?? CALENDLY_BLUE);
  const locIcon       = LOCATION_ICONS[event.location?.type] ?? <CalendarMonthOutlinedIcon fontSize="small" />;
  const isVideo       = ['zoom','google_conference','microsoft_teams_conference','gotomeeting','webex'].includes(event.location?.type);
  const locationIsPhone = looksLikePhone(event.location?.detail);
  const eventPhone    = event.invitees?.[0]?.phone_display || event.location?.detail || null;
  const eventPhoneForSave = looksLikePhone(eventPhone) ? eventPhone : null;
  const pharmacy      = getPharmacyName(event);

  const handleConnect = async (chemist) => {
    setConnecting(true); setConnectError(null);
    try {
      const result = await phappsRequest(API_PATHS.CALENDLY_CONNECT_CHEMIST(event.id), 'POST', { chemist_id: chemist.chemistID });
      onChemistConnected(event.id, result.chemist);
      setShowChemistSearch(false); setChemistQuery('');
      if (eventPhoneForSave && chemist.PHUUID)
        setSavePhoneData({ phone: eventPhoneForSave, chemistUUID: chemist.PHUUID, chemistName: chemist.chemistName });
    } catch { setConnectError('Failed to connect. Please try again.'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    setConnecting(true); setConnectError(null);
    try { await phappsRequest(API_PATHS.CALENDLY_CONNECT_CHEMIST(event.id), 'POST', { chemist_id: null }); onChemistConnected(event.id, null); }
    catch { setConnectError('Failed to disconnect. Please try again.'); }
    finally { setConnecting(false); }
  };

  return (
    <>
      <Drawer anchor="right" open={!!event} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', sm: 440 } } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Coloured header band */}
          <Box sx={{ background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`, px: 2.5, pt: 2.5, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {pharmacy && (
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                    {pharmacy}
                  </Typography>
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: '#fff' }}>
                  {event.event_name}
                </Typography>
                {canceled && (
                  <Chip label="Canceled" size="small" sx={{ mt: 0.75, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.65rem' }} />
                )}
              </Box>
              <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }, mt: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ px: 2.5, py: 2, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Manager */}
            {event.manager && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: event.manager.accent_color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.manager.name}</Typography>
              </Box>
            )}

            {/* Date/time */}
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f9fafb', border: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Date &amp; Time (Perth)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                {fmtPerthDateTime(event.start_time_utc)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                → {fmtPerthTime(event.end_time_utc)}
              </Typography>
            </Box>

            {/* Location */}
            {event.location?.type && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>{locIcon}</Box>
                {isVideo && event.location.detail ? (
                  <Link href={event.location.detail} target="_blank" rel="noopener noreferrer" variant="body2"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    Join meeting <LinkOutlinedIcon sx={{ fontSize: 13 }} />
                  </Link>
                ) : locationIsPhone ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatAuPhone(event.location.detail)}</Typography>
                    <PhoneButton phone={event.location.detail} />
                  </Box>
                ) : (
                  <Typography variant="body2">{event.location.detail || event.location.type}</Typography>
                )}
              </Box>
            )}

            <Divider />

            {/* Linked chemist */}
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                Linked Chemist
              </Typography>

              {event.chemist ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 1.5, border: `1.5px solid ${CALENDLY_BLUE}33`, bgcolor: '#eff6ff' }}>
                  <StoreIcon sx={{ fontSize: 16, color: CALENDLY_BLUE, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, color: CALENDLY_BLUE }} noWrap>{event.chemist.name}</Typography>
                  <Tooltip title="Open chemist page">
                    <IconButton size="small" onClick={() => navigate(`/admin-functions/chemist/${encodeURIComponent(event.chemist.uuid)}`)}
                      sx={{ p: 0.4, color: CALENDLY_BLUE }}>
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove link">
                    <IconButton size="small" disabled={connecting} onClick={handleDisconnect}
                      sx={{ p: 0.4, color: '#9ca3af', '&:hover': { color: '#ef4444' } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant={showChemistSearch ? 'contained' : 'outlined'}
                  startIcon={showChemistSearch ? <CloseIcon sx={{ fontSize: 14 }} /> : <LinkIcon sx={{ fontSize: 14 }} />}
                  onClick={() => { setShowChemistSearch(v => !v); setChemistQuery(''); setConnectError(null); }}
                  sx={{
                    textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                    ...(showChemistSearch
                      ? { backgroundColor: CALENDLY_BLUE, '&:hover': { backgroundColor: '#0052cc' } }
                      : { borderColor: CALENDLY_BLUE, color: CALENDLY_BLUE, '&:hover': { backgroundColor: '#eff6ff' } }),
                  }}
                >
                  {showChemistSearch ? 'Cancel' : 'Connect to chemist'}
                </Button>
              )}

              {connectError && <Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>{connectError}</Alert>}

              <Collapse in={showChemistSearch && !event.chemist}>
                <Box sx={{ mt: 1, border: '1px solid #bfdbfe', borderRadius: 1.5, bgcolor: '#eff6ff', p: 1 }}>
                  <TextField fullWidth size="small" placeholder="Search chemist…"
                    value={chemistQuery} onChange={e => setChemistQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                    sx={{ mb: 1, bgcolor: '#fff', borderRadius: 1 }} autoFocus />
                  {chemistsLoading ? (
                    <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={18} sx={{ color: CALENDLY_BLUE }} /></Box>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {filteredChemists.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ py: 1, textAlign: 'center', display: 'block' }}>
                          {chemistQuery ? 'No chemists match.' : 'No chemists found.'}
                        </Typography>
                      ) : filteredChemists.map(c => (
                        <Box key={c.chemistID} onClick={() => !connecting && handleConnect(c)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
                            borderRadius: 1, cursor: connecting ? 'not-allowed' : 'pointer', bgcolor: '#fff',
                            border: '1px solid #e5e7eb',
                            '&:hover': { borderColor: CALENDLY_BLUE, bgcolor: '#eff6ff' },
                            transition: 'all 0.1s',
                          }}
                        >
                          <StoreIcon sx={{ fontSize: 14, color: CALENDLY_BLUE, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }} noWrap>{c.chemistName}</Typography>
                          {connecting && <CircularProgress size={12} sx={{ color: CALENDLY_BLUE }} />}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>

            <Divider />

            {/* Invitees */}
            {event.invitees?.length > 0 ? event.invitees.map((inv, i) => (
              <Box key={i}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>{inv.name}</Typography>
                {inv.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{inv.email}</Typography>
                    <EmailButton email={inv.email} />
                  </Box>
                )}
                {inv.phone_display && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="body2">{formatAuPhone(inv.phone_display)}</Typography>
                    <PhoneButton phone={inv.phone_display} />
                  </Box>
                )}
                {inv.questions_answers?.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {inv.questions_answers.map((qa, qi) => (
                      <Box key={qi}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{qa.question}</Typography>
                        {looksLikePhone(qa.answer) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">{formatAuPhone(qa.answer)}</Typography>
                            <PhoneButton phone={qa.answer} />
                          </Box>
                        ) : (
                          <Typography variant="body2">{qa.answer || '—'}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                {!canceled && (inv.cancel_url || inv.reschedule_url) && (
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    {inv.reschedule_url && (
                      <Button size="small" variant="outlined" href={inv.reschedule_url} target="_blank" rel="noopener noreferrer"
                        sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: CALENDLY_BLUE, color: CALENDLY_BLUE }}>
                        Reschedule
                      </Button>
                    )}
                    {inv.cancel_url && (
                      <Button size="small" variant="outlined" href={inv.cancel_url} target="_blank" rel="noopener noreferrer"
                        sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: '#ef4444', color: '#ef4444' }}>
                        Cancel booking
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No invitee data</Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      <SavePhoneDialog
        open={!!savePhoneData}
        onClose={() => setSavePhoneData(null)}
        phone={savePhoneData?.phone}
        chemistUUID={savePhoneData?.chemistUUID}
        chemistName={savePhoneData?.chemistName}
      />
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const VIEW_KEY = 'calendly_calendar_view';

export default function AdminCalendlyPage() {
  const isPHAdmin = sessionStorage.getItem('IsPHAdmin') === 'true';

  const [managers, setManagers]         = useState([]);
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [activeTab, setActiveTab]       = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [syncing, setSyncing]           = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [snack, setSnack]               = useState(null);
  const [allChemists, setAllChemists]   = useState([]);
  const [chemistsLoading, setChemistsLoading] = useState(false);
  const [search, setSearch]             = useState('');
  const [expandedSlots, setExpandedSlots] = useState(false); // user dismissed the banner
  const calendarRef   = useRef(null);
  const loadedYearsRef = useRef(new Set());

  // Persist the user's last chosen view
  const [calendarView, setCalendarView] = useState(
    () => localStorage.getItem(VIEW_KEY) || 'timeGridWeek'
  );

  const fetchEventsForYear = useCallback(async (year) => {
    if (loadedYearsRef.current.has(year)) return;
    loadedYearsRef.current.add(year);
    try {
      const evts = await phappsRequest(API_PATHS.CALENDLY_EVENTS, 'GET', null, {
        status: 'all', from: `${year}-01-01`, to: `${year}-12-31`,
      });
      setEvents(prev => {
        const incoming  = evts?.events || [];
        const existingIds = new Set(prev.map(e => e.id));
        return [...prev, ...incoming.filter(e => !existingIds.has(e.id))];
      });
    } catch (err) { console.error('[Calendly] fetchEventsForYear:', err.message); }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    const year = new Date().getFullYear();
    loadedYearsRef.current = new Set([year]);
    try {
      const [mgrs, evts] = await Promise.all([
        phappsRequest(API_PATHS.CALENDLY_MANAGERS, 'GET'),
        phappsRequest(API_PATHS.CALENDLY_EVENTS, 'GET', null, {
          status: 'all', from: `${year}-01-01`, to: `${year}-12-31`,
        }),
      ]);
      setManagers(mgrs || []);
      setEvents(evts?.events || []);
    } catch (err) { setError(err?.message || 'Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    setChemistsLoading(true);
    phappsRequest(API_PATHS.CHEMIST_LIST, 'GET')
      .then(data => setAllChemists(data?.chemists ?? []))
      .catch(() => {})
      .finally(() => setChemistsLoading(false));
  }, []);

  const handleChemistConnected = useCallback((eventId, chemist) => {
    setEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, chemist: chemist ?? null } : ev));
    setSelectedEvent(prev => prev?.id === eventId ? { ...prev, chemist: chemist ?? null } : prev);
  }, []);

  const handleDatesSet = useCallback((dateInfo) => {
    fetchEventsForYear(dateInfo.start.getFullYear());
    // Persist the view the user switched to
    if (dateInfo.view?.type) {
      setCalendarView(dateInfo.view.type);
      localStorage.setItem(VIEW_KEY, dateInfo.view.type);
    }
  }, [fetchEventsForYear]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await phappsRequest(API_PATHS.CALENDLY_SYNC, 'POST', {});
      const errCount = result.errors?.length ?? 0;
      setSnack({
        severity: errCount > 0 ? 'warning' : 'success',
        msg: `Sync complete — ${result.events_synced} events, ${result.invitees_synced} invitees` +
          (errCount > 0 ? ` (${errCount} errors)` : ''),
      });
      loadedYearsRef.current = new Set();
      fetchData();
    } catch (err) { setSnack({ severity: 'error', msg: err?.message || 'Sync failed' }); }
    finally { setSyncing(false); }
  };

  // ── Derived data ────────────────────────────────────────────────────────────

  const activeManagerId = activeTab === 0 ? null : managers[activeTab - 1]?.manager_id;

  const visibleEvents = useMemo(() =>
    events
      .filter(ev => activeManagerId == null || ev.manager?.id === activeManagerId)
      .filter(ev => showCanceled || ev.status !== 'canceled')
      .filter(ev => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const pharmacy = getPharmacyName(ev);
        return (
          (pharmacy || '').toLowerCase().includes(q) ||
          (ev.event_name || '').toLowerCase().includes(q) ||
          (ev.invitees?.[0]?.name || '').toLowerCase().includes(q) ||
          (ev.invitees?.[0]?.email || '').toLowerCase().includes(q)
        );
      }),
    [events, activeManagerId, showCanceled, search]
  );

  // Stats
  const statsAll = events.filter(ev => ev.status !== 'canceled');

  // Smart time window — detect if any active visible events fall outside default range
  const hasEarlyEvents = useMemo(() =>
    visibleEvents.some(ev => ev.status !== 'canceled' && (getPerthHour(ev.start_time_utc) ?? 7) < 6),
    [visibleEvents]
  );
  const hasLateEvents = useMemo(() =>
    visibleEvents.some(ev => ev.status !== 'canceled' && (getPerthHour(ev.end_time_utc) ?? 19) > 20),
    [visibleEvents]
  );
  const hasOutOfRangeEvents = hasEarlyEvents || hasLateEvents;
  const showBanner = hasOutOfRangeEvents && !expandedSlots;

  const slotMinTime = expandedSlots ? '00:00:00' : DEFAULT_SLOT_MIN;
  const slotMaxTime = expandedSlots ? '24:00:00' : DEFAULT_SLOT_MAX;

  // FullCalendar events
  const fcEvents = visibleEvents.map(ev => {
    const canceled = ev.status === 'canceled';
    const color    = canceled ? CANCELED_COLOR : (ev.manager?.accent_color ?? CALENDLY_BLUE);
    const pharmacy = getPharmacyName(ev);
    const invName  = ev.invitees?.[0]?.name;
    const title    = pharmacy ?? invName ?? ev.event_name;
    return {
      id: String(ev.id),
      title,
      start: ev.start_time_utc,
      end:   ev.end_time_utc,
      backgroundColor: color,
      borderColor:     color,
      textColor: '#fff',
      extendedProps: { eventData: ev, pharmacy, invName },
    };
  });

  // Custom event content — FullCalendar (current version) no longer falls back to its
  // default rendering when this callback returns null/undefined, so every view must be
  // handled explicitly or events render as empty blocks.
  const renderEventContent = (info) => {
    const { pharmacy, invName } = info.event.extendedProps;
    const label    = pharmacy || invName || info.event.title;
    const viewType = info.view.type;

    // Week / day grids — richer stacked content
    if (viewType.startsWith('timeGrid')) {
      return (
        <Box sx={{ px: 0.5, py: 0.25, overflow: 'hidden', width: '100%' }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.2, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {info.timeText && <span style={{ opacity: 0.85, marginRight: 4 }}>{info.timeText}</span>}
            {label}
          </Typography>
          {(pharmacy && invName) && (
            <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {invName}
            </Typography>
          )}
        </Box>
      );
    }

    // Month grid — compact single-line: dot + time + title (mirrors FullCalendar's default look)
    if (viewType.startsWith('dayGrid')) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, overflow: 'hidden', width: '100%' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0, opacity: 0.9 }} />
          {info.timeText && (
            <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', opacity: 0.85, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {info.timeText}
            </Typography>
          )}
          <Typography component="span" noWrap sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
          </Typography>
        </Box>
      );
    }

    // List view — FullCalendar renders the time/colour marker in their own columns;
    // we only need to supply the title text.
    return (
      <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>
        {label}
      </Typography>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: 1400, mx: 'auto', width: '100%' }}>

      {/* ── Fixed header ── */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 3, pb: 0 }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${CALENDLY_BLUE} 0%, #4338ca 100%)`,
              boxShadow: `0 3px 10px ${CALENDLY_BLUE}40`,
            }}>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 24, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', lineHeight: 1.25 }}>Calendly Bookings</Typography>
              {!loading && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, mt: 0.25 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: CALENDLY_BLUE }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#4b5563' }}>
                    {statsAll.length.toLocaleString()} active booking{statsAll.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search pharmacy, name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9ca3af' }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant={showCanceled ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setShowCanceled(v => !v)}
              sx={{
                textTransform: 'none',
                ...(showCanceled
                  ? { backgroundColor: '#ef4444', '&:hover': { backgroundColor: '#dc2626' }, borderColor: '#ef4444' }
                  : { borderColor: '#ef4444', color: '#ef4444', '&:hover': { backgroundColor: '#fef2f2' } }),
              }}
            >
              {showCanceled ? 'Hide Cancelled' : 'Show Cancelled'}
            </Button>
            {isPHAdmin && (
              <Tooltip title="Full sync from Calendly API">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={handleSync} disabled={syncing || loading} size="small"
                  >
                    {syncing ? 'Syncing…' : 'Sync'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Out-of-range banner */}
        {showBanner && (
          <Alert
            severity="info"
            icon={<WarningAmberIcon fontSize="small" />}
            sx={{ mb: 1.5, py: 0.5 }}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="small" color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}
                  onClick={() => setExpandedSlots(true)}>
                  Show full day
                </Button>
                <IconButton size="small" color="inherit" onClick={() => setExpandedSlots(true)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            Some bookings are outside 6 AM – 8 PM and are currently hidden below the visible area.
          </Alert>
        )}
        {expandedSlots && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}
            action={
              <Button size="small" color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}
                onClick={() => setExpandedSlots(false)}>
                Collapse to 6 AM–8 PM
              </Button>
            }>
            Showing full 24-hour day.
          </Alert>
        )}

        {/* Manager tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>All<Chip label={statsAll.length} size="small" sx={{ height: 18, fontSize: 11, ml: 0.5, bgcolor: '#e5e7eb', color: '#374151' }} /></Box>} />
            {managers.map(m => (
              <Tab key={m.manager_id} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.accent_color, flexShrink: 0 }} />
                  {m.name}
                  {m.upcoming_event_count > 0 && (
                    <Chip label={m.upcoming_event_count} size="small" sx={{ height: 18, fontSize: 11, ml: 0.5, bgcolor: m.accent_color, color: '#fff' }} />
                  )}
                </Box>
              } />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, minHeight: 0, px: 3, pt: 2, pb: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Upcoming strip */}
            <UpcomingStrip events={visibleEvents} onSelect={setSelectedEvent} />

            {/* Calendar */}
            <Box sx={{
              flex: 1, minHeight: 0,
              '& .fc': { height: '100%' },
              '& .fc-event': { cursor: 'pointer', borderRadius: '4px !important' },
              '& .fc-daygrid-event .fc-event-time':  { fontSize: '0.62rem' },
              '& .fc-daygrid-event .fc-event-title': { fontSize: '0.62rem' },
              '& .fc-timegrid-event .fc-event-time':  { display: 'none' }, // handled in custom renderer
              '& .fc-timegrid-event .fc-event-title': { fontSize: '0.62rem' },
              '& .fc-timegrid-event .fc-event-main':  { padding: '1px 2px' },
              '& .fc-timegrid-slot': { height: '3.2em' },
              '& .fc-timegrid-slot-minor': { borderTopStyle: 'dashed' },
              '& .fc-toolbar-title': { fontSize: '1.1rem', fontWeight: 700 },
              '& .fc-button': { textTransform: 'capitalize' },
              '& .fc-timegrid-now-indicator-line': { borderColor: '#ef4444', borderWidth: '2px' },
              '& .fc-timegrid-now-indicator-arrow': { borderTopColor: '#ef4444', borderBottomColor: '#ef4444' },
            }}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, luxonPlugin]}
                initialView={calendarView}
                timeZone={PERTH_TZ}
                headerToolbar={{
                  left:   'prev,next today',
                  center: 'title',
                  right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                }}
                events={fcEvents}
                eventDisplay="block"
                eventContent={renderEventContent}
                eventClick={(info) => setSelectedEvent(info.event.extendedProps.eventData)}
                datesSet={handleDatesSet}
                height="100%"
                allDaySlot={false}
                nowIndicator
                scrollTime="07:00:00"
                slotMinTime={slotMinTime}
                slotMaxTime={slotMaxTime}
                eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
                buttonText={{ today: 'Today', month: 'Month', week: 'Week', day: 'Day', list: 'List' }}
                noEventsContent="No bookings found"
              />
            </Box>
          </>
        )}
      </Box>

      <EventDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onChemistConnected={handleChemistConnected}
        allChemists={allChemists}
        chemistsLoading={chemistsLoading}
      />

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled" sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
